'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Send, CheckCircle2, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ButtonSpinner } from '@/components/ui/button-spinner';
import { cn } from '@/lib/utils';
import { SELECT_BASE_STYLES } from '@/lib/styles/select-styles';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * Client component for contact form that handles form submission.
 *
 * This component handles the interactive form logic (state, validation, submission).
 * The static content (contact info cards, FAQs) is rendered server-side.
 *
 * @example
 * ```tsx
 * <ContactForm />
 * ```
 *
 * @see {@link apps/web/src/app/contact/page.tsx} - Server-side contact page that uses this component
 */
export function ContactForm() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create mailto link with form data
      const subject = encodeURIComponent(formData.subject || 'Contact from Email Ferret Website');
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      );
      const mailtoLink = `mailto:support@emailferret.io?subject=${subject}&body=${body}`;

      // Open email client
      window.location.href = mailtoLink;

      // Small delay to ensure navigation happens
      await new Promise((resolve) => setTimeout(resolve, 100));

      setIsSubmitted(true);
      setIsSubmitting(false);
      toast({
        title: 'Email client opened',
        description: 'Please send your message from your email client.',
      });
    } catch {
      setIsSubmitting(false);
      toast({
        title: 'Error',
        description:
          'Could not open email client. Please email us directly at support@emailferret.io',
        variant: 'destructive',
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">
          Your email client should have opened. Please send your message from there.
        </p>
        <Button
          onClick={() => {
            setIsSubmitted(false);
            setFormData({ name: '', email: '', subject: '', message: '' });
          }}
          variant="outline"
          className="hover:bg-muted"
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30">
          <MessageSquare className="h-5 w-5 text-white" />
        </div>
        Send us a Message
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        <div>
          <Label htmlFor="name" className="text-foreground font-semibold">
            Name
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
            aria-required="true"
            className="mt-2 bg-card/50 border-border focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
            placeholder="Your name"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-foreground font-semibold">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            aria-required="true"
            className="mt-2 bg-card/50 border-border focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <Label htmlFor="subject" className="text-foreground font-semibold">
            Subject
          </Label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={cn(
              SELECT_BASE_STYLES,
              'mt-2 w-full px-4 py-3 rounded-lg bg-card/50 border-border focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all'
            )}
            aria-label="Subject"
          >
            <option value="">Select a topic...</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Technical Support">Technical Support</option>
            <option value="Billing Question">Billing Question</option>
            <option value="Feature Request">Feature Request</option>
            <option value="Bug Report">Bug Report</option>
            <option value="Partnership Inquiry">Partnership Inquiry</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <Label htmlFor="message" className="text-foreground font-semibold">
            Message
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            aria-required="true"
            rows={6}
            className="mt-2 bg-card/50 border-border focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 resize-none transition-all"
            placeholder="Tell us how we can help..."
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn-gradient-primary"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <ButtonSpinner />
              Opening email client...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          By clicking &quot;Send Message&quot;, your default email client will open with a
          pre-filled message.
        </p>
      </form>
    </>
  );
}
